import { useTestimonials } from "@/hooks/useTestimonials";
import { Star } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { usePageContent } from "@/hooks/usePageContent";

function TestimonialCard({ authorName, testimonialText, rating, authorRole, authorPhotoUrl }: {
  authorName: string;
  testimonialText: string;
  rating: number;
  authorRole?: string | null;
  authorPhotoUrl?: string | null;
}) {
  return (
    <div className="bg-white/8 hover:bg-white/12 border border-white/10 hover:border-white/20 rounded-2xl p-7 flex flex-col h-full transition-all duration-300">
      {/* Stars */}
      <div className="flex gap-1 mb-5">
        {[...Array(5)].map((_, i) => (
          <Star key={i} className={`w-4 h-4 ${i < rating ? "text-yellow-400 fill-current" : "text-white/20"}`} />
        ))}
      </div>

      {/* Quote */}
      <p className="text-white/80 flex-1 leading-relaxed font-primary font-light text-sm italic mb-6">
        "{testimonialText}"
      </p>

      {/* Author */}
      <div className="flex items-center gap-3 pt-5 border-t border-white/10">
        <Avatar className="h-10 w-10 border-2 border-white/20">
          <AvatarImage src={authorPhotoUrl || undefined} alt={authorName} />
          <AvatarFallback className="bg-white/20 text-white text-sm font-bold font-primary">
            {authorName.charAt(0)}
          </AvatarFallback>
        </Avatar>
        <div>
          <p className="font-bold text-white text-sm font-primary">{authorName}</p>
          {authorRole && <p className="text-white/40 text-xs font-primary">{authorRole}</p>}
        </div>
      </div>
    </div>
  );
}

export function TestimonialsSection() {
  const { testimonials, isLoading } = useTestimonials(true);
  const { getText } = usePageContent('home');

  const sectionLabel = getText('testimonials', 'label', 'Resultados reais');
  const title        = getText('testimonials', 'title', 'O que dizem meus pacientes');
  const googleReviewsRating = getText('testimonials', 'google_reviews_rating', '5.0');
  const googleReviewsLabel = getText('testimonials', 'google_reviews_label', '100+ avaliações no Google');
  const googleReviewsUrl   = getText('testimonials', 'google_reviews_url', '');
  const hasGoogleReviewsUrl = /^https?:\/\//i.test(googleReviewsUrl);

  return (
    <section id="depoimentos" className="py-24 bg-[#162f22] relative overflow-hidden">
      {/* Decoração */}
      <div className="absolute inset-0 opacity-30"
        style={{ backgroundImage: "radial-gradient(circle at 20% 80%, #29BD68 0%, transparent 50%), radial-gradient(circle at 80% 20%, #1696B2 0%, transparent 50%)" }} />

      <div className="container mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-6">
          <div>
            <p className="text-brand-softYellow font-bold text-sm uppercase tracking-widest font-primary mb-3">
              {sectionLabel}
            </p>
            <h2 className="text-4xl md:text-5xl font-extrabold text-white font-primary leading-tight">
              {title}
            </h2>
          </div>
          {/* Rating badge */}
          {hasGoogleReviewsUrl ? (
            <a href={googleReviewsUrl} target="_blank" rel="noopener noreferrer" aria-label="Ver avaliações no Google"
              className="flex-shrink-0 bg-white/10 border border-white/15 rounded-2xl px-6 py-4 text-center block hover:bg-white/15 transition-colors">
              <div className="flex justify-center gap-1 mb-1">
                {[1,2,3,4,5].map(i => <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />)}
              </div>
              <p className="text-3xl font-extrabold text-white font-primary">{googleReviewsRating}</p>
              <p className="text-white/50 text-xs font-primary">{googleReviewsLabel}</p>
            </a>
          ) : (
            <div className="flex-shrink-0 bg-white/10 border border-white/15 rounded-2xl px-6 py-4 text-center">
            <div className="flex justify-center gap-1 mb-1">
              {[1,2,3,4,5].map(i => <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />)}
            </div>
            <p className="text-3xl font-extrabold text-white font-primary">{googleReviewsRating}</p>
            <p className="text-white/50 text-xs font-primary">{googleReviewsLabel}</p>
            </div>
          )}
        </div>

        {/* Cards */}
        {isLoading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {[1,2,3].map(i => <Skeleton key={i} className="h-48 bg-white/10" />)}
          </div>
        ) : testimonials.length === 0 ? (
          <p className="text-white/40 text-center font-primary">Em breve, depoimentos dos nossos pacientes.</p>
        ) : (
          <div className={`grid gap-6 ${
            testimonials.length === 1 ? "sm:grid-cols-1 max-w-md mx-auto" :
            testimonials.length === 2 ? "sm:grid-cols-2 max-w-3xl mx-auto" :
            "sm:grid-cols-2 lg:grid-cols-3"
          }`}>
            {testimonials.map((t) => (
              <TestimonialCard
                key={t.id}
                authorName={t.author_name}
                testimonialText={t.testimonial_text}
                rating={t.rating}
                authorRole={t.author_role}
                authorPhotoUrl={t.author_photo_url}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
